import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Squares2X2Icon, CubeIcon, ShoppingBagIcon, BuildingStorefrontIcon, TruckIcon,
  UsersIcon, InboxIcon, BanknotesIcon, ChartBarIcon, PhotoIcon, FilmIcon,
  Cog6ToothIcon, MagnifyingGlassIcon, BellIcon, Bars3Icon, ArrowRightOnRectangleIcon,
  HomeIcon, CommandLineIcon, ClipboardDocumentListIcon, ShieldCheckIcon, RectangleStackIcon,
  StarIcon, TicketIcon, ArrowUturnLeftIcon, LifebuoyIcon, IdentificationIcon, MegaphoneIcon, SparklesIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useCurrency } from "@/context/CurrencyContext";
import { EmailHealthIndicator } from "@/components/admin/EmailHealthIndicator";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { CommandPalette } from "@/components/admin/CommandPalette";
import { Breadcrumbs } from "@/components/Breadcrumbs";

export const ADMIN_GROUPS = [
  { label: "Commerce", items: [
    { key: "overview", label: "Overview", icon: Squares2X2Icon },
    { key: "products", label: "Products", icon: CubeIcon, badge: "pending_products" },
    { key: "orders", label: "Orders", icon: ShoppingBagIcon, badge: "new_orders" },
    { key: "returns", label: "Returns & Refunds", icon: ArrowUturnLeftIcon, badge: "pending_returns" },
    { key: "age", label: "Age Verification", icon: IdentificationIcon, badge: "pending_age_verifications" },
  ]},
  { label: "Marketplace", items: [
    { key: "tenants", label: "Tenants / Stores", icon: RectangleStackIcon, badge: "pending_sellers" },
    { key: "sellers", label: "Seller Approvals", icon: BuildingStorefrontIcon, badge: "pending_sellers" },
    { key: "verifications", label: "Seller Verification", icon: ShieldCheckIcon },
    { key: "delivery", label: "Delivery", icon: TruckIcon, badge: "pending_delivery" },
  ]},
  { label: "CRM", items: [
    { key: "users", label: "Customers", icon: UsersIcon },
    { key: "reviews", label: "Reviews", icon: StarIcon },
    { key: "inbox", label: "Inbox", icon: InboxIcon, badge: "inbox_open" },
    { key: "support", label: "Support", icon: LifebuoyIcon, badge: "support_open" },
  ]},
  { label: "Finance", items: [
    { key: "revenue", label: "Finance & Revenue", icon: BanknotesIcon },
    { key: "subscriptions", label: "Subscriptions", icon: RectangleStackIcon },
    { key: "partners", label: "Affiliates", icon: MegaphoneIcon },
    { key: "finance", label: "Payments & Payouts", icon: BanknotesIcon },
  ]},
  { label: "Analytics", items: [
    { key: "analytics", label: "Live Analytics", icon: ChartBarIcon },
  ]},
  { label: "Marketing", items: [
    { key: "coupons", label: "Coupons", icon: TicketIcon },
    { key: "content", label: "Site Content", icon: PhotoIcon },
    { key: "hero-promos", label: "Hero Promos", icon: SparklesIcon },
    { key: "videoads", label: "Video Ads", icon: FilmIcon },
    { key: "imageads", label: "Image Ads", icon: PhotoIcon },
  ]},
  { label: "Administration", items: [
    { key: "audit", label: "Audit Logs", icon: ClipboardDocumentListIcon },
    { key: "roles", label: "Roles & Access", icon: ShieldCheckIcon, superAdminOnly: true },
    { key: "settings", label: "Settings", icon: Cog6ToothIcon },
  ]},
];

const NAV_FLAT = ADMIN_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label })));

export const AdminShell = ({ active, onNavigate, badges = {}, title, subtitle, actions, children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { lang, setLang, langs } = useI18n();
  const { currency, setCurrency, currencies } = useCurrency();
  const [mobileNav, setMobileNav] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key && e.key.toLowerCase() === "k") { e.preventDefault(); setCmdOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (key) => { setMobileNav(false); onNavigate(key); };
  const initials = (user?.name || "A").trim().charAt(0).toUpperCase();
  const inboxOpen = badges.inbox_open || 0;
  const activeItem = NAV_FLAT.find((i) => i.key === active);
  const crumbs = activeItem ? [{ label: activeItem.group }, { label: activeItem.label }] : [];

  const Side = () => (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-5 border-b border-border/60 shrink-0">
        <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-black text-lg font-display">M</div>
        <div className="min-w-0">
          <p className="font-display font-black text-sm leading-tight truncate">Xurray<span className="text-muted-foreground">Mart</span></p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Admin Console</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4">
        {ADMIN_GROUPS.map((group) => (
          <div key={group.label}>
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">{group.label}</p>
            <div className="space-y-0.5">
              {group.items.filter((item) => !item.superAdminOnly || user?.admin_role === "super_admin").map((item) => {
                const Icon = item.icon;
                const isActive = active === item.key;
                const count = item.badge ? badges[item.badge] : 0;
                return (
                  <button key={item.key} data-testid={`admin-nav-${item.key}`} onClick={() => go(item.key)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                    <Icon className="w-[18px] h-[18px] shrink-0" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {count > 0 && <span className={`min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center ${isActive ? "bg-primary-foreground/20" : "bg-primary text-primary-foreground"}`}>{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-border/60 space-y-0.5">
        <button data-testid="admin-nav-storefront" onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"><HomeIcon className="w-[18px] h-[18px]" />View storefront</button>
        <button data-testid="admin-logout" onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"><ArrowRightOnRectangleIcon className="w-[18px] h-[18px]" />Sign out</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/40">
      <ImpersonationBanner />
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-card/70 backdrop-blur-xl border-r border-border/60 z-30 flex-col shadow-sm"><Side /></aside>

      {mobileNav && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileNav(false)} />
          <div className="relative w-64 bg-card h-full shadow-2xl"><Side /></div>
        </div>
      )}

      <div className="lg:pl-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 h-16 bg-card/70 backdrop-blur-xl border-b border-border/60 flex items-center gap-3 px-4 sm:px-6 shadow-sm">
          <Button variant="ghost" size="icon" className="lg:hidden" data-testid="admin-mobile-menu" aria-label="Open navigation menu" onClick={() => setMobileNav(true)}><Bars3Icon className="w-5 h-5" /></Button>

          <button data-testid="admin-search-trigger" onClick={() => setCmdOpen(true)}
            className="w-10 justify-center sm:flex-1 sm:max-w-md sm:justify-start flex items-center gap-2 h-10 sm:px-3 rounded-full bg-muted/60 border border-border/60 text-sm text-muted-foreground hover:bg-muted transition-colors">
            <MagnifyingGlassIcon className="w-4 h-4" />
            <span className="hidden sm:block flex-1 text-left">Search or jump to…</span>
            <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-background border border-border/60 text-[10px] font-medium"><CommandLineIcon className="w-3 h-3" />K</kbd>
          </button>

          <div className="flex items-center gap-1 ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button data-testid="admin-currency" variant="ghost" size="icon" aria-label="Change currency" className="rounded-full font-bold text-sm">{currencies.find((c) => c.code === currency)?.symbol || "€"}</Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">{currencies.map((c) => <DropdownMenuItem key={c.code} onClick={() => setCurrency(c.code)} className={currency === c.code ? "bg-accent/50 font-semibold" : ""}><span className="mr-2 w-4 text-center font-bold">{c.symbol}</span>{c.code}</DropdownMenuItem>)}</DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button data-testid="admin-language" variant="ghost" size="icon" aria-label="Change language" className="rounded-full"><span className="text-lg leading-none">{langs.find((l) => l.code === lang)?.flag || "🌐"}</span></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">{langs.map((l) => <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} className={lang === l.code ? "bg-accent/50 font-semibold" : ""}><span className="mr-2 text-lg leading-none">{l.flag}</span>{l.label}</DropdownMenuItem>)}</DropdownMenuContent>
            </DropdownMenu>
            <EmailHealthIndicator />
            <Button data-testid="admin-notifications" variant="ghost" size="icon" aria-label={`Notifications${inboxOpen > 0 ? `, ${inboxOpen} unread` : ""}`} className="rounded-full relative" onClick={() => go("inbox")}>
              <BellIcon className="w-5 h-5" />
              {inboxOpen > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center">{inboxOpen > 9 ? "9+" : inboxOpen}</span>}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="admin-profile-menu" className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-full hover:bg-muted transition-colors">
                  <span className="w-8 h-8 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center text-sm">{initials}</span>
                  <span className="hidden sm:block text-sm font-medium max-w-[120px] truncate">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => go("settings")}><Cog6ToothIcon className="w-4 h-4 mr-2" />Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/")}><HomeIcon className="w-4 h-4 mr-2" />Storefront</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }}><ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {crumbs.length > 0 && <div className="mb-4"><Breadcrumbs items={crumbs} onHome={() => go("overview")} /></div>}
            {(title || actions) && (
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                  {title && <h1 className="font-display font-bold text-2xl sm:text-3xl">{title}</h1>}
                  {subtitle && <p className="text-muted-foreground text-sm mt-0.5">{subtitle}</p>}
                </div>
                {actions}
              </div>
            )}
            <AnimatePresence mode="wait">
              <motion.div key={active}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22, ease: "easeOut" }}>
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>

      <CommandPalette open={cmdOpen} setOpen={setCmdOpen} navItems={NAV_FLAT} onNavigate={onNavigate} />
    </div>
  );
};
